import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../api/auth/AuthContext';
import './Navigation.css';

const EMPLOYEE_PATH = '/empleado';

// ── SVG Icons ───────────────────────────────────────────────────────────────

const PhoneIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.61 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.92 16z"/>
  </svg>
);

const MailIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);

const UserIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="8" r="4"/>
    <path d="M6 20c0-3.314 2.686-6 6-6s6 2.686 6 6"/>
  </svg>
);

const ChevronDown = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

// ── Account Menu ─────────────────────────────────────────────────────────────

const AccountMenu: React.FC = () => {
  const location = useLocation();
  const { account, authType, ssoChecking, loginInternal, loginExternal, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const isEmployeePage = location.pathname === EMPLOYEE_PATH;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (ssoChecking) {
    return (
      <div></div>
    );
  }

  if (!account) {
    if (isEmployeePage) {
      return (
        <div className="nav-account">
          <button
            className="nav-account-btn nav-account-btn--login"
            onClick={loginInternal}
            title="Iniciar sesión como empleado (cuenta abcscale.mx)"
          >
            SSO Login
          </button>
        </div>
      );
    }
    return (
      <div className="nav-account">
        <button
          className="nav-account-btn nav-account-btn--login"
          onClick={loginExternal}
          title="Iniciar sesión como cliente"
        >
          Inicia Sesión
        </button>
      </div>
    );
  }

  return (
    <div className="nav-account" ref={ref}>
      <button className="nav-account-btn" onClick={() => setOpen(v => !v)}>
        <UserIcon />
        {account.name ?? account.username}
        <ChevronDown />
      </button>

      {open && (
        <div className="nav-account-dropdown">
          <div className="nav-account-info">
            <div className="nav-account-name">{account.name ?? account.username}</div>
            <div className="nav-account-type">
              {authType === 'internal' ? 'Empleado' : 'Cliente'}
            </div>
          </div>
          <hr className="nav-account-divider" />

          {authType === 'internal' && (
            <Link
              className="nav-account-item"
              to="/admin"
              onClick={() => setOpen(false)}
            >
              Portal de Empleados
            </Link>
          )}

          <button className="nav-account-item nav-account-item--logout" onClick={logout}>
            Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
};

// ── Navigation ───────────────────────────────────────────────────────────────

const envLabel: string | null = (() => {
  const e = process.env.REACT_APP_ENV;
  if (!e || e === 'prod' || e === 'production') return null;
  if (!e || e === 'dev' || e === 'development') return "Development";
  return e.toUpperCase();
})();

const Navigation: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsMenuOpen(false);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`site-header${scrolled ? ' site-header--scrolled' : ''}`}>

      {/* Top utility bar */}
      <div className="nav-topbar">
        <div className="nav-topbar-inner">
          <div className="nav-topbar-contact">
            <a href="tel:+523336349050" className="nav-topbar-link">
              <PhoneIcon /> (33) 3634-9050
            </a>
            <a href="mailto:abc@abcscale.mx" className="nav-topbar-link">
              <MailIcon /> abc@abcscale.mx
            </a>
            {envLabel && (
              <span className="nav-env-badge" aria-label={`Entorno: ${envLabel}`}>
                {envLabel}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav className="navigation" aria-label="Navegación principal">
        <div className="nav-container">

          <Link to="/" className="nav-logo" aria-label="ABC Básculas — Inicio">
            <img src="/logo.png" alt="ABC Básculas" />
          </Link>

          <div className={`nav-menu${isMenuOpen ? ' nav-menu-open' : ''}`}>

            <Link to="/quienes-somos" className="nav-link">QUIÉNES SOMOS</Link>
            <Link to="/servicios" className="nav-link">SERVICIOS</Link>
            <Link to="/automatizacion" className="nav-link">AUTOMATIZACIÓN</Link>

            {/* Products megamenu */}
            <div className="nav-item nav-dropdown">
              <Link to="/productos" className="nav-link nav-link--has-dropdown">
                PRODUCTOS <ChevronDown />
              </Link>
              <div className="dropdown-panel">
                <div className="dropdown-col">
                  <div className="dropdown-col-title">Básculas</div>
                  <Link to="/productos/basculas-camioneras">Básculas Camioneras</Link>
                  <Link to="/productos/basculas-contadoras">Básculas Contadoras</Link>
                  <Link to="/productos/basculas-de-banda">Básculas de Banda</Link>
                  <Link to="/productos/basculas-de-banco">Básculas de Banco</Link>
                  <Link to="/productos/basculas-de-piso">Básculas de Piso</Link>
                  <Link to="/productos/basculas-de-monorriel">Básculas de Monorriel</Link>
                  <Link to="/productos/basculas-de-montacargas">Básculas de Montacargas</Link>
                  <Link to="/productos/basculas-para-ferrocarril">Básculas para Ferrocarril</Link>
                  <Link to="/productos/basculas-verificadoras">Básculas Verificadoras</Link>
                </div>
                <div className="dropdown-col">
                  <div className="dropdown-col-title">Instrumentación</div>
                  <Link to="/productos/celdas-de-carga">Celdas de Carga</Link>
                  <Link to="/productos/dinamometros">Dinamómetros</Link>
                  <Link to="/productos/equipos-agropecuarios">Equipos Agropecuarios</Link>
                  <Link to="/productos/equipo-de-fuerza">Equipo de Fuerza</Link>
                  <Link to="/productos/impresoras">Impresoras</Link>
                  <Link to="/productos/indicadores-de-peso">Indicadores de Peso</Link>
                </div>
              </div>
            </div>

            <Link to="/software" className="nav-link">SOFTWARE</Link>
            <Link to="/clientes" className="nav-link">CLIENTES</Link>
            <Link to="/contacto" className="nav-link">CONTACTO</Link>

          </div>

          <AccountMenu />

          <button
            className={`nav-toggle${isMenuOpen ? ' nav-toggle--open' : ''}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={isMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

        </div>
      </nav>
    </header>
  );
};

export default Navigation;
